import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { of, Subscription, switchMap, tap } from 'rxjs';
import { weaponsApi } from 'src/app/shared/constants/api.constants';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { caliber } from '../../shared/enums/caliber';
import { HttpBaseService, ToastService } from '@evento-ng/libs';
import {
  getWeaponTypeKeyByValue,
  weaponType,
} from '../../shared/enums/weapon-type';
import {
  getLicenseTypeKeyByValue,
  licenseType,
} from '../../shared/enums/license-type';
import { Toast } from '../../shared/enums/toast';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-update-weapon',
  templateUrl: './update-weapon.component.html',
  styleUrls: ['./update-weapon.component.scss'],
})
export class UpdateWeaponComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];
  weapon: any;
  loadingGetWeapon: boolean = false;
  weaponForm: UntypedFormGroup;
  caliber = [
    {
      id: caliber.NINE_MM,
      name: caliber.NINE_MM,
    },
    {
      id: caliber.TWILIGHT_GA,
      name: caliber.TWILIGHT_GA,
    },
  ];
  weaponType = [
    {
      id: getWeaponTypeKeyByValue(weaponType.PISTOLS),
      name: weaponType.PISTOLS,
    },
    {
      id: getWeaponTypeKeyByValue(weaponType.SHOTGUN),
      name: weaponType.SHOTGUN,
    },
  ];

  licenseType = [
    {
      id: getLicenseTypeKeyByValue(licenseType.CARRY),
      name: licenseType.CARRY,
    },
    {
      id: getLicenseTypeKeyByValue(licenseType.HAVING),
      name: licenseType.HAVING,
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private dataService: HttpBaseService,
    private formBuilder: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private router: Router,
    private toast: ToastService,
    private recaptchaV3Service: ReCaptchaV3Service,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadingGetWeapon = true;
    this.subs.push(
      this.route.params
        .pipe(
          switchMap((params: Params) => {
            if (!params.id) {
              return of(null);
            }
            return this.dataService.get(`${weaponsApi}/get/${params.id}`);
          })
        )
        .subscribe((res) => {
          this.loadingGetWeapon = false;
          this.weapon = res;
          this.initForm();
        })
    );
  }

  initForm() {
    this.weaponForm = this.formBuilder.group({
      fullName: [
        this.weapon.fullName,
        Validators.compose([Validators.required]),
      ],
      nid: [this.weapon.nid, Validators.compose([Validators.required])],
      weaponType: [
        this.weapon.weaponType,
        Validators.compose([Validators.required]),
      ],
      licenseType: [
        this.weapon.licenseType,
        Validators.compose([Validators.required]),
      ],
      weaponNumber: [
        this.weapon.weaponNumber,
        Validators.compose([Validators.required]),
      ],
      brand: [this.weapon.brand, Validators.compose([Validators.required])],
      caliber: [this.weapon.caliber, Validators.compose([Validators.required])],
      licenseExpiryAt: [
        this.weapon.licenseExpiryAt,
        Validators.compose([Validators.required]),
      ],
      version: [this.weapon.version, Validators.compose([Validators.required])],
      permitsExpiryAt: [this.weapon.permitsExpiryAt, Validators.compose([])],
      permitsNumber: [this.weapon.permitsNumber, Validators.compose([])],
      recaptchaToken: [],
    });
  }

  submit(): void {
    this.spinner.show();
    this.recaptchaV3Service
      .execute('updateWeapon')
      .pipe(
        switchMap((token) => {
          const formValue = {
            ...this.weaponForm.value,
            recaptchaToken: token,
          };

          return this.dataService.put(
            `${weaponsApi}/update/${this.weapon.id}`,
            formValue
          );
        }),
        tap((_) => {
          this.spinner.hide();
          this.toast.show(
            'Success',
            this.translate.instant('updated successfully'),
            {
              classname: Toast.success,
            }
          );
          this.router.navigate(['../../my-weapons'], {
            relativeTo: this.route,
          });
        })
      )
      .subscribe();
  }

  onChangeLicenseType(event: any) {
    let permitsExpiryAt: FormControl = this.weaponForm.get(
      'permitsExpiryAt'
    ) as FormControl;
    let permitsNumber: FormControl = this.weaponForm.get(
      'permitsNumber'
    ) as FormControl;
    if (event === licenseType.CARRY) {
      permitsExpiryAt.setValue('');
      permitsNumber.setValue('');
      permitsExpiryAt.setValidators(null);
      permitsNumber.setValidators(null);
    } else if (event === licenseType.HAVING) {
      permitsExpiryAt.setValidators(Validators.required);
      permitsNumber.setValidators(Validators.required);
    }
    permitsExpiryAt.updateValueAndValidity();
    permitsNumber.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
